import type { AmenityType } from '@/config/objects/amenity-types';
import {
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  PageContainer,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Wifi } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

const tabs = [
  { label: 'Overview', path: 'overview', icon: Wifi },
  { label: 'Amenities', path: 'amenities', icon: Wifi },
];

export function AmenityTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: AmenityType }>({
    queryKey: ['AmenityType', id],
    queryFn: () => apiClient.get(`/amenity-types/${id}`),
    enabled: !!id,
  });

  const amenityType = data?.data;

  const deleteMutation = useMutation({
    mutationFn: async (amenityTypeId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/amenity-types/${amenityTypeId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['AmenityType'] });
        navigate('/amenity-types');
      } else {
        throw new Error(response.error || 'Failed to delete amenity type');
      }
    },
  });

  const handleDelete = () => {
    if (!amenityType) return;
    toast.promise(deleteMutation.mutateAsync(amenityType.id), {
      loading: 'Deleting amenity type...',
      success: 'Amenity type deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete amenity type',
    });
  };

  // Redirect base path to /overview
  useEffect(() => {
    if (id) {
      const basePath = `/amenity-types/${id}`;
      if (location.pathname === basePath || location.pathname === `${basePath}/`) {
        navigate(`${basePath}/overview`, { replace: true });
      }
    }
  }, [id, location.pathname, navigate]);

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Amenity Types', href: '/amenity-types' },
    { label: amenityType?.name || 'Amenity Type', current: true },
  ];

  const basePath = `/amenity-types/${id}`;
  const imageUrl = amenityType?.featuredImage?.url ?? null;

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-2">Amenity Type Not Found</h2>
          <p className="text-muted-foreground">
            The requested amenity type could not be found.
          </p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader
        actions={
          amenityType && (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )
        }
      >
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      <DetailPageHero
        isLoading={isLoading}
        category={{ label: 'Amenity Type', icon: Wifi }}
        title={amenityType?.name || 'Amenity Type'}
        image={{
          url: imageUrl,
          alt: amenityType?.name || 'Amenity Type',
          fallbackIcon: Wifi,
        }}
        tabs={tabs}
        basePath={basePath}
      >
        <Outlet context={{ amenityType, isLoading }} />
      </DetailPageHero>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Amenity Type"
        description={`Are you sure you want to delete "${amenityType?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </PageContainer>
  );
}
